import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiUrls from "../../api";
import Navigation from "../../common/components/Navigation/Navigation";
import DeviceConnectionBar from "../../features/deviceInstaller/components/DeviceConnectionBar";
import InstallProgressModal from "../../features/deviceInstaller/components/InstallProgressModal";
import UninstallButton from "../../features/deviceInstaller/components/UninstallButton";
import ScreenMirror from "../../features/deviceInstaller/components/ScreenMirror";
import {
  getConnectedDevice,
  getInstalledPackages,
  getPackageScanStatus,
  getEnduserBuilds,
  setEnduserBuilds,
  IEnduserBuildInfo,
} from "../../features/deviceInstaller/state/deviceInstallerSlice";
import { getBuilds } from "../../features/builds/state/buildsSlice";
import { IBuild } from "../../model/model";
import { isWebUsbSupported } from "../../features/deviceInstaller/utils/browserCompat";
import { installApkFromUrl } from "../../features/deviceInstaller/utils/adbInstaller";
import MediaPushModal from "../../features/deviceInstaller/components/MediaPushModal";
import {
  detectDeviceFamily,
  isPlatformCompatible,
} from "../../features/deviceInstaller/utils/platformCompat";
import { mapToEnduserBuildInfos } from "../../features/deviceInstaller/utils/buildStatus";
import { AppDispatch } from "../../common/state/store";
import { SearchInput } from "../../common/components/SearchInput";
import { getCurrentUser } from "../../features/profile/state/profileSlice";
import {
  CheckIcon,
  WarningCircleIcon,
  DownloadArrowIcon,
  DevicePlainIcon,
  Spinner,
} from "../../features/deviceInstaller/components/icons";
import LaunchButton from "../../features/deviceInstaller/components/LaunchButton";
import { launchApp } from "../../features/deviceInstaller/utils/appLauncher";
import { extractProjectGroupId } from "../../features/deviceInstaller/utils/packageHelpers";
import {
  fetchAllCurrentElements,
  fetchSpecificProjectGroupWithProjects,
  fetchProjectGroupMedias,
  getProjectGroupPermissionsFromServer,
  setCurrentProjectGroupPermissions,
} from "../../features/projectGroups/state/projectGroupsSlice";
import SpatialStudioSection from "../../features/deviceInstaller/components/SpatialStudioSection";
import DesktopAppsTable from "../../features/deviceInstaller/components/DesktopAppsTable";
import AgentConnectionBar from "../../features/deviceInstaller/components/AgentConnectionBar";
import { useAgentConnection } from "../../features/deviceInstaller/hooks/useAgentConnection";

interface WixarPackage {
  packageName: string;
  installedVersion: number | null;
  latestBuild: IBuild | null;
  enduserBuild: IEnduserBuildInfo | null;
  latestVersion: number | null;
  projectGroupName: string;
  isOutdated: boolean;
}

/** Match installed packages against known Wixar builds (creator + enduser),
 *  filtering enduser builds by connected device compatibility */
function useWixarPackages(): WixarPackage[] {
  const installedPackages = useSelector(getInstalledPackages);
  const builds = useSelector(getBuilds) as IBuild[];
  const enduserBuilds = useSelector(getEnduserBuilds);
  const device = useSelector(getConnectedDevice);

  const deviceFamily = device?.status === "ready" ? detectDeviceFamily(device.productName) : null;

  const buildsByPackage: Record<string, IBuild> = {};
  for (const build of builds) {
    if (!build.package_name || build.status !== "Published") continue;
    const existing = buildsByPackage[build.package_name];
    if (!existing || build.version > existing.version) {
      buildsByPackage[build.package_name] = build;
    }
  }

  // Find the best enduser build per packageName that's compatible with the connected device
  const enduserBuildsByPackage: Record<string, IEnduserBuildInfo> = {};
  for (const eb of Object.values(enduserBuilds)) {
    if (deviceFamily && !isPlatformCompatible(eb.targetPlatform, deviceFamily)) continue;
    const existing = enduserBuildsByPackage[eb.packageName];
    if (!existing || eb.version > existing.version) {
      enduserBuildsByPackage[eb.packageName] = eb;
    }
  }

  const WIXAR_PACKAGE_PREFIXES = ["com.WideWebVR.", "com.Wixar."];
  const isWixarPackage = (name: string) =>
    WIXAR_PACKAGE_PREFIXES.some((prefix) => name.startsWith(prefix));

  const wixarPackages: WixarPackage[] = [];

  for (const [pkgName, installed] of Object.entries(installedPackages)) {
    if (!isWixarPackage(pkgName)) continue;
    const latestBuild = buildsByPackage[pkgName] ?? null;
    const enduserBuild = enduserBuildsByPackage[pkgName] ?? null;

    const latestVersion = latestBuild?.version ?? enduserBuild?.version ?? null;
    const displayName =
      latestBuild?.project_group?.name ?? enduserBuild?.projectGroupName ?? pkgName;

    wixarPackages.push({
      packageName: pkgName,
      installedVersion: installed.versionCode,
      latestBuild,
      enduserBuild,
      latestVersion,
      projectGroupName: displayName,
      isOutdated:
        latestVersion !== null &&
        installed.versionCode !== null &&
        installed.versionCode < latestVersion,
    });
  }

  return wixarPackages;
}

function PackageRow({ pkg }: { pkg: WixarPackage }) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showMediaPushModal, setShowMediaPushModal] = useState(false);

  const handleInstall = async () => {
    if (pkg.latestBuild) {
      setShowModal(true);
      const build = pkg.latestBuild;
      const fileName = build.name || `wixar_build_${build.id}.apk`;
      await installApkFromUrl(build.s3_url, fileName, build.id, dispatch, pkg.packageName);
    } else if (pkg.enduserBuild) {
      setShowModal(true);
      const eb = pkg.enduserBuild;
      const fileName = `wixar_build_${eb.buildId}.apk`;
      await installApkFromUrl(eb.s3Url, fileName, eb.buildId, dispatch, pkg.packageName);
    }
  };

  const hasUpdateSource = pkg.latestBuild !== null || pkg.enduserBuild !== null;
  const projectGroupId =
    pkg.latestBuild?.project_group?.id ?? extractProjectGroupId(pkg.packageName);

  const handleNavigateToProjectGroup = async () => {
    if (!projectGroupId) return;
    await dispatch(
      fetchSpecificProjectGroupWithProjects({
        projectGroupId,
        componentId: "projectGroupListComponent",
      }),
    );
    dispatch(
      fetchAllCurrentElements({
        projectGroupId,
        componentId: "projectGroupListComponent",
      }),
    );
    dispatch(
      fetchProjectGroupMedias({
        projectGroupId,
        componentId: "projectGroupMediasComponentId",
      }),
    );
    dispatch(getProjectGroupPermissionsFromServer({ projectGroupId })).then((res) => {
      dispatch(setCurrentProjectGroupPermissions((res.payload as any).data));
    });
    navigate(`/courses/${projectGroupId}/list`);
  };

  const isLinkable = hasUpdateSource && projectGroupId;

  const handleMediaPush = () => {
    if (!projectGroupId) return;
    setShowMediaPushModal(true);
  };

  return (
    <tr className="border-b last:border-0">
      <td className="py-3 px-4">
        {isLinkable ? (
          <button
            onClick={handleNavigateToProjectGroup}
            className="font-medium text-blue-600 hover:underline cursor-pointer text-left"
          >
            {pkg.projectGroupName}
          </button>
        ) : (
          <div className="font-medium">{pkg.projectGroupName}</div>
        )}
        <div className="text-xs text-gray-400">{pkg.packageName}</div>
      </td>
      <td className="py-3 px-4">
        {pkg.installedVersion !== null ? `V${pkg.installedVersion}` : "—"}
      </td>
      <td className="py-3 px-4">{pkg.latestVersion !== null ? `V${pkg.latestVersion}` : "—"}</td>
      <td className="py-3 px-4">
        {pkg.isOutdated ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-600 border border-orange-200">
            <WarningCircleIcon className="w-3 h-3" />
            {t("deviceManager.outdated")}
          </span>
        ) : hasUpdateSource ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-50 text-green-600 border border-green-200">
            <CheckIcon className="w-3 h-3" />
            {t("deviceManager.upToDate")}
          </span>
        ) : (
          <span className="text-xs text-gray-400">{t("deviceManager.unknown")}</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <LaunchButton onLaunch={() => launchApp(pkg.packageName)} />
          {pkg.isOutdated && hasUpdateSource && (
            <button
              onClick={handleInstall}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-white bg-green-500 hover:opacity-90 transition-colors"
            >
              <DownloadArrowIcon />
              {t("deviceInstaller.update")} V{pkg.latestVersion}
            </button>
          )}
          {projectGroupId && hasUpdateSource && (
            <button
              onClick={handleMediaPush}
              title={t("deviceInstaller.mediaManageButton")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
            >
              <DownloadArrowIcon />
              {t("deviceInstaller.mediaManageButton")}
            </button>
          )}
          <UninstallButton packageName={pkg.packageName} confirmBeforeUninstall />
        </div>
        {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
        {showMediaPushModal && projectGroupId && (
          <MediaPushModal
            projectGroupId={projectGroupId}
            packageName={pkg.packageName}
            onClose={() => setShowMediaPushModal(false)}
          />
        )}
      </td>
    </tr>
  );
}

export default function DeviceManager() {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const device = useSelector(getConnectedDevice);
  const scanStatus = useSelector(getPackageScanStatus);
  const currentUser = useSelector(getCurrentUser);
  const isReady = device?.status === "ready";
  const wixarPackages = useWixarPackages();
  const supported = isWebUsbSupported();
  const [search, setSearch] = useState("");
  const TAB_STORAGE_KEY = "deviceManager.lastTab";
  const [tab, setTabState] = useState<"device" | "desktop">(() => {
    const saved = sessionStorage.getItem(TAB_STORAGE_KEY);
    return saved === "device" || saved === "desktop" ? saved : "device";
  });
  const setTab = (t: "device" | "desktop") => {
    sessionStorage.setItem(TAB_STORAGE_KEY, t);
    setTabState(t);
  };
  const {
    status: agentStatus,
    agentInfo,
    retry: retryAgent,
    removeApp: removeAgentApp,
  } = useAgentConnection();

  const isEnduser = currentUser?.role?.name === "Enduser";

  // Fetch enduser builds on mount to populate version info (enduser only)
  useEffect(() => {
    if (!isEnduser) return;

    const fetchEnduserBuilds = async () => {
      try {
        const response = await axios.get(apiUrls.stats.enduserAllApkBuilds);
        const builds = response.data || [];
        const buildMap = mapToEnduserBuildInfos(builds);
        if (Object.keys(buildMap).length > 0) {
          dispatch(setEnduserBuilds(buildMap));
        }
      } catch {
        // Non-blocking — enduser builds are supplementary info
      }
    };

    fetchEnduserBuilds();
  }, [dispatch, isEnduser]);

  const filteredPackages = search.trim()
    ? wixarPackages.filter(
        (pkg) =>
          pkg.packageName.toLowerCase().includes(search.toLowerCase()) ||
          pkg.projectGroupName.toLowerCase().includes(search.toLowerCase()),
      )
    : wixarPackages;

  return (
    <>
      <Navigation title={t("deviceManager.title")} />
      <div className="mt-10 pl-[9rem] overflow-auto w-full h-full personalize-scroll-visible">
        <div className="mt-2 mr-5 px-4 sm:px-8 md:px-12">
          <h1 className="text-xl font-bold text-gray-800 mb-2">{t("deviceManager.header")}</h1>
          <p className="text-gray-600 text-sm mb-1">{t("deviceManager.description")}</p>
          <p
            className={`text-xs mb-6 ${
              tab === "device" && !isReady ? "text-amber-600" : "invisible"
            }`}
          >
            {t("deviceManager.devModeNotice")}
          </p>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-gray-200">
            <button
              onClick={() => setTab("device")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                tab === "device"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t("deviceManager.tabDevice")}
              {isReady && <span className="w-2 h-2 rounded-full bg-green-500" />}
            </button>
            <button
              onClick={() => setTab("desktop")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                tab === "desktop"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t("deviceManager.tabDesktop")}
              {agentStatus === "connected" && (
                <span className="w-2 h-2 rounded-full bg-green-500" />
              )}
            </button>
          </div>

          {/* ─── Device tab (existing) ─── */}
          {tab === "device" && (
            <>
              {/* Connection bar */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {supported ? (
                  <DeviceConnectionBar />
                ) : (
                  <span className="text-gray-400 text-sm">
                    {t("deviceInstaller.browserNotSupported")}
                  </span>
                )}
              </div>

              {/* Screen mirror */}
              {isReady && <ScreenMirror />}

              {/* Spatial Studio */}
              {isReady && <SpatialStudioSection />}

              {/* Package list */}
              {isReady && (
                <>
                  {/* Show scanning spinner only on initial scan (no packages yet) */}
                  {scanStatus === "scanning" && wixarPackages.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Spinner color="green" className="w-4 h-4" />
                      {t("deviceManager.scanning")}
                    </div>
                  )}

                  {(scanStatus === "done" || wixarPackages.length > 0) && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-800">
                          {t("deviceManager.installedApps", { count: wixarPackages.length })}
                        </h2>
                        <SearchInput
                          value={search}
                          onChange={setSearch}
                          placeholder={t("general.search")}
                          className="w-64"
                        />
                      </div>

                      {filteredPackages.length === 0 ? (
                        <p className="text-gray-400 text-sm italic">
                          {t("deviceManager.noAppsFound")}
                        </p>
                      ) : (
                        <table className="w-full text-sm text-left text-gray-800 border-collapse">
                          <thead className="border-b font-medium bg-gray-100">
                            <tr>
                              <th className="py-2 px-4">{t("deviceManager.colPackage")}</th>
                              <th className="py-2 px-4">
                                {t("deviceManager.colInstalledVersion")}
                              </th>
                              <th className="py-2 px-4">{t("deviceManager.colLatestVersion")}</th>
                              <th className="py-2 px-4">{t("deviceManager.colStatus")}</th>
                              <th className="py-2 px-4">{t("deviceManager.colActions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPackages.map((pkg) => (
                              <PackageRow key={pkg.packageName} pkg={pkg} />
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Placeholder when no device */}
              {!isReady && supported && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <DevicePlainIcon className="w-16 h-16 mb-4" />
                  <p className="text-sm">{t("deviceManager.connectPrompt")}</p>
                </div>
              )}
            </>
          )}

          {/* ─── Desktop tab ─── */}
          {tab === "desktop" && (
            <>
              <div className="mb-6">
                <AgentConnectionBar
                  status={agentStatus}
                  agentInfo={agentInfo}
                  onRetry={retryAgent}
                />
              </div>

              {agentStatus === "connected" && agentInfo && (
                <>
                  <SpatialStudioSection
                    mode="agent"
                    agentInfo={agentInfo}
                    onRemoveApp={removeAgentApp}
                  />
                  <DesktopAppsTable
                    agentInfo={agentInfo}
                    onRefresh={retryAgent}
                    onRemoveApp={removeAgentApp}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
